/// <amd-module name="typeahead/core/Suggestion"/>
import {IListItem} from "typeahead/core/IListItem";
import {Optional} from "typeahead/core/util/Optional";

export interface IEssId {
    sourceName: string;
    sourceId: string;
}

export interface IRegionNames {
    displayName?: string;
    fullName: string;
    lastSearchName: string;
}

export interface IRegionMetadata {
    redirect: string;
}

export interface IExtraSearchParam {
    field: string;
    value: string;
}

export interface IDisplayGroup {
    groupName: string;
    label: string;
}

export interface IFreetextConcepts {
    id: number;
    metaDataJSON: string;
    type: string;
}

export interface ISuggestionResult extends IListItem {
    index: string;
    coordinates?: {
        lat: string;
        long: string;
    };
    category: string;
    icon?: string;
    regionNames: IRegionNames;
    showAsHierarchy?: boolean;
    type: string;
    displayGroup?: IDisplayGroup;
    /* the label if present is to be per individual suggestion - non-clickable */
    label?: string;
    style?: string;
    parentPosition?: string;
}

// Interface to enforce rule for all suggestions expected from ESS
export interface IEssSuggestionResult extends ISuggestionResult {
    cityId: string; /*CityId for hotel*/
    essId: IEssId; /*HotelID*/
    gaiaId: string; /*RegionId*/
    hierarchyInfo: {
        airport: {
            airportCode: string;
            airportId: string;
            multicity: string;
        }
        country: {
            name: string;
            isoCode2: string;
            isoCode3: string;
        },
        isChild: string;
        relation: string[];
        relateTo: string;
        multicity: string;
        rails: {
            stationCode: string;
            pointOfSupply: string;
        }
        nearby: {
            locDist: string;
        }
        hotels: {
            hotelFilterId: string;
        }
    };
    subtype: string;
    hotelAddress?: {
        street?: string;
        city?: string;
        province?: string;
    };
    history: IHistory;
    next: string;
    sort: any; // TODO: get the correct type
    regionMetadata: IRegionMetadata;
    timestamp: string;
    relation: string[];
    showAsHierarchy: boolean;
    targetHtmlElements: string[];
    extraSearchParams: IExtraSearchParam[];
    /* the concepts are provided in ESS response only for freetext suggestions*/
    concepts?: IFreetextConcepts;
}

// Interface to specify attributes of a recent hostory suggestion as inorporated into IEssSuggestionResult
export interface IHistory {
    search: {
        regionId: string;
        destination: {
            regionname: string;
        }
        checkoutdate: number;
        checkindate: number;
        rooms: Array<{
            numadults: number;
            noofchildren: number;
            agesofchildren: number[];
        }>
    };
    timestamp: number;
}

export enum SuggestionCategory {
    GEOLOCATION, USERHISTORY, FLIGHTSBOOKING, GOOGLEMAPS,
}

export interface ISuggestionData {
    coordinates: {
        lat: string;
        long: string;
    };
    airportCode: string;
    city: string;
    hotelId: string;
    redirectPath: string; // This is a hack for experiment 15289 (Redirect Cuba Pkg Search) & will be removed in future.
    regionId: string;
    regionType: string;
    stationCode: string;
    pointOfSupply: string;
    countryCode: string;
    category: string;
    heds: string;
    extraSearchParam: IExtraSearchParam;
    concepts?: IFreetextConcepts;
}

// Suggestion class upon instantiation populates the underlying data attributes with undefined
// unless populated in the respective sub class indicating source eg: ESSSuggestion
export abstract class Suggestion {
    constructor (protected data: ISuggestionResult, protected hasChild: boolean = false) {}

    public getSourceName(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getSourceId(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getRegionId(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getTla(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getAirportId(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getRelateTo(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getType(): Optional<string> {
        return Optional.ofNullable(this.data.type);
    }

    public getRegionNames(): Optional<IRegionNames> {
        return Optional.ofNullable(this.data.regionNames);
    }

    public getFullName(): Optional<string> {
        return Optional.ofNullable(this.getRegionNames().orElse(<IRegionNames> {}).fullName);
    }

    public getLastSearchName(): Optional<string> {
        return Optional.ofNullable(this.getRegionNames().orElse(<IRegionNames> {}).lastSearchName);
    }

    public getDisplayName(): Optional<string> {
        return Optional.ofNullable(this.getRegionNames().orElse(<IRegionNames> {}).displayName);
    }

    public getDisplayText(): string {
        return this.getLastSearchName().orElse(this.getDisplayName().orElse(""));
    }

    public getIndex(): number {
        return parseInt(this.data.index, 10);
    }

    public getPosition(): number {
        if (this.data.parentPosition) {
            return parseInt(this.data.parentPosition, 10);
        }
        return this.getIndex() + 1;
    }

    public getSuggestionHierarchyType(): Optional<string> {
        return Optional.of("parent");
    }

    public getHierarchyCode(): string {
        // Send the new instrumentation only for hotel LOB
        /**
         * Old nomenclature
         * C0 indicates item is parent
         * C1 indicates item is child
         * P0 indicates item has no child
         * P1 indicates item has child
         */

        // C indicates a chid suggestion
        // P indicates it is a parent suggestion having child/ children
        // D indicates the suggestion is neither a parent nor a child
        // L indicates level of item selected
        let hierarchyCode: string = "";
        if (this.getSuggestionHierarchyType().get() === "child") {
            // a child cannot have children
            hierarchyCode = "C"; // "P0C1";
        } else if (this.getSuggestionHierarchyType().get() === "parent" && this.hasChild) {
            hierarchyCode = "P"; // "P1C0";
        } else {
            // Default hierarchy code : has no Children, is a parent  if the suggestions are shown
            hierarchyCode = "D"; // "P0C0";
        }
        return hierarchyCode + "L#" + (!isNaN(this.getPosition()) ? this.getPosition() : "-");
    }

    public getRelationInfo(): Optional<string> {
        return Optional.of("-");
    }

    public getHotelId(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getLatLong(): Optional<string> {
        return Optional.ofNullable(this.data.coordinates ?
        this.data.coordinates.lat + "," + this.data.coordinates.long : undefined);
    }

    public getTypeId(): Optional<string> {
        return Optional.of("-");
    }

    public getCity(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getMultiCity(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getSubType(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getHasChild(): Optional<boolean> {
        return Optional.of(this.hasChild);
    }

    public getCategory(): Optional<string> {
        return Optional.ofNullable(this.data.category);
    }

    public getStationCode(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getPointOfSupply(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getCountryName(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getCountryIsoCode2(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    public getCountryIsoCode3(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    /* returns an Optional containing either:
     * -- "true" if the selected suggestion is a HEDS (Hotel Expedia Doesn't Sell)
     * -- empty for all other selected suggestions
     * if "true", HEDS=true will be sent to HSRP
     * if empty, no query parameter will be sent
     */
    public getIsHeds(): Optional<string> {
        return Optional.empty<string>();
    }

    public isAddrAutoComplete(): boolean {
        return false;
    }

    public getExtraSearchParams(): Optional<IExtraSearchParam[]> {
        return Optional.ofNullable(undefined);
    }

    public getSearchName(): Optional<string> {
        return this.getRelationInfo().orElse("") === "hotelfilter" ?
            this.getFullName() : Optional.ofNullable(undefined);
    }

    // This is a hack for experiment 15289 (Redirect Cuba Pkg Search) and will be removed in future.
    // The population of value for the path is done in ESSSuggestion
    public getRedirectPath(): Optional<string> {
        return Optional.ofNullable(undefined);
    }
    // The population of value for the concepts is done in ESSSuggestion
    public getConcepts(): Optional<IFreetextConcepts> {
        return Optional.ofNullable(undefined);
    }

    public getNext(): Optional<string> {
        return Optional.ofNullable(undefined);
    }

    // This function populates the data field that is exposed to all the clients
    public getSuggestionData(): Optional<string> {
        return Optional.of(JSON.stringify({
            airportCode: this.getTla().orElse(undefined),
            category: this.getCategory().orElse(undefined),
            city: this.getCity().orElse(undefined),
            concepts: this.getConcepts().orElse(undefined),
            coordinates: this.data.coordinates,
            countryCode: this.getCountryIsoCode2().orElse(undefined),
            extraSearchParams: this.getExtraSearchParams().orElse(undefined),
            fullName: this.getFullName().orElse(undefined),
            heds: this.getIsHeds().orElse(undefined),
            hotelId: this.getHotelId().orElse(undefined),
            pointOfSupply: this.getPointOfSupply().orElse(undefined),
            // This is a hack for experiment 15289 (Redirect Cuba Pkg Search) and will be removed in future.
            redirectPath: this.getRedirectPath().orElse(undefined),
            regionId: this.getRegionId().orElse(undefined),
            regionType: this.getType().orElse(undefined),
            stationCode: this.getStationCode().orElse(undefined),
        }));
    }

    public abstract shouldTriggerDomElementChangeOnSelection(): boolean;

    public getSearchedRegion(): string {
        return this.getRelateTo().orElse(this.getSourceId().orElse("-"));
    }

    public getSelectedRegion(): string {
        return this.getRelateTo().orElse("") ? this.getSourceId().orElse("-") : this.getAirportId().orElse("-");
    }
}
